import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const destinations = [
  'Villa del Palmar',
  'Villa La Estancia',
  'Villa del Arco',
  'Villa La Valencia',
  'Hotel Santa Fe'
];

const VisitorRegistry = () => {
  const [roomNumber, setRoomNumber] = useState('');
  const [numberOfPeople, setNumberOfPeople] = useState('');
  const [vehicleBrand, setVehicleBrand] = useState('');
  const [vehiclePlate, setVehiclePlate] = useState('');
  const [vehicleColor, setVehicleColor] = useState('');
  const [comments, setComments] = useState('');
  const [selectedDestinations, setSelectedDestinations] = useState([]);
  const [registries, setRegistries] = useState([]);
  const [loading, setLoading] = useState(false);
  const { user, logout } = useAuth();

  useEffect(() => {
    // No fetch registries for security users, only for admins
  }, []);

  const handleDestinationToggle = (destination) => {
    setSelectedDestinations(prev =>
      prev.includes(destination)
        ? prev.filter(d => d !== destination)
        : [...prev, destination]
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!roomNumber.trim() || !numberOfPeople || selectedDestinations.length === 0) {
      toast.error('Por favor complete los campos obligatorios');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/security/registry`, {
        room_number: roomNumber.trim(),
        number_of_people: parseInt(numberOfPeople),
        vehicle_brand: vehicleBrand.trim() || null,
        vehicle_plate: vehiclePlate.trim() || null,
        vehicle_color: vehicleColor.trim() || null,
        comments: comments.trim() || null,
        destination: selectedDestinations
      });

      toast.success('Registro creado exitosamente');
      
      // Reset form
      setRoomNumber('');
      setNumberOfPeople('');
      setVehicleBrand('');
      setVehiclePlate('');
      setVehicleColor('');
      setComments('');
      setSelectedDestinations([]);
    } catch (error) {
      console.error('Error creating registry:', error);
      toast.error('Error al crear registro');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="visitor-registry-container" data-testid="visitor-registry">
      <div className="admin-header">
        <div className="header-left">
          <h1 data-testid="registry-title">Registro de Visitantes</h1>
          <p className="user-info">Usuario: <strong>{user?.username}</strong> | Rol: <strong>Seguridad</strong></p>
        </div>
        <div className="header-actions">
          <Link to="/security" className="back-button" data-testid="back-security-btn">
            Volver al Panel
          </Link>
          <button onClick={logout} className="logout-button" data-testid="logout-btn">
            Cerrar Sesión
          </button>
        </div>
      </div>

      <div className="registry-content-single">
        <div className="form-section-wide" data-testid="registry-form">
          <h2>Registrar Nueva Visita</h2>
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="roomNumber">Número de Habitación *</label>
              <input
                id="roomNumber"
                type="text"
                value={roomNumber}
                onChange={(e) => setRoomNumber(e.target.value)}
                placeholder="Ej: 101, 205, etc."
                data-testid="room-number-input"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="numberOfPeople">Número de Personas *</label>
              <input
                id="numberOfPeople"
                type="number"
                min="1"
                value={numberOfPeople}
                onChange={(e) => setNumberOfPeople(e.target.value)}
                placeholder="Ej: 2, 4, etc."
                data-testid="number-people-input"
                required
              />
            </div>

            <div className="form-group">
              <label>Lugar donde se dirige *</label>
              <div className="checkbox-group">
                {destinations.map(dest => (
                  <label key={dest} className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={selectedDestinations.includes(dest)}
                      onChange={() => handleDestinationToggle(dest)}
                      data-testid={`dest-${dest.replace(/\s/g, '-').toLowerCase()}`}
                    />
                    <span>{dest}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="vehiclePlate">Número Económico o Placa</label>
              <input
                id="vehiclePlate"
                type="text"
                value={vehiclePlate}
                onChange={(e) => setVehiclePlate(e.target.value.toUpperCase())}
                placeholder="Ej: ABC-1234"
                data-testid="vehicle-plate-input"
              />
            </div>

            <div className="form-group">
              <label htmlFor="vehicleBrand">Marca de Vehículo</label>
              <input
                id="vehicleBrand"
                type="text"
                value={vehicleBrand}
                onChange={(e) => setVehicleBrand(e.target.value)}
                placeholder="Ej: Toyota, Honda, etc."
                data-testid="vehicle-brand-input"
              />
            </div>

            <div className="form-group">
              <label htmlFor="vehicleColor">Color de Vehículo</label>
              <input
                id="vehicleColor"
                type="text"
                value={vehicleColor}
                onChange={(e) => setVehicleColor(e.target.value)}
                placeholder="Ej: Blanco, Negro, Azul"
                data-testid="vehicle-color-input"
              />
            </div>

            <div className="form-group">
              <label htmlFor="comments">Comentarios</label>
              <textarea
                id="comments"
                value={comments}
                onChange={(e) => setComments(e.target.value)}
                placeholder="Observaciones adicionales..."
                rows="4"
                data-testid="comments-input"
              />
            </div>

            <button
              type="submit"
              className="submit-button"
              disabled={loading}
              data-testid="create-registry-btn"
            >
              {loading ? 'Registrando...' : 'Registrar Visita'}
            </button>
          </form>
          
          <div className="info-message">
            <p>ℹ️ Los registros creados serán visibles para los administradores en el historial de visitas.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VisitorRegistry;
